import { createContextApi, createCursorPagedEndpoint, invalidatesOne, providesOne, providesList } from '@quickbee/store';

// src/index.ts
var MARKETPLACE_TAGS = ["Listing", "Collection", "Storefront"];
var marketplaceEndpoints = (build) => ({
  // ─── Operator: the moderation queue ─────────────────────────────────────────
  //
  // There is deliberately no create-listing and no edit-listing endpoint, here or
  // anywhere. Listings are a projection fed by commerce-service's outbox, not an
  // aggregate the operator authors: a rejected listing is rejected *here* and the
  // merchant fixes it *there*.
  /**
   * The listing queue — everything the network's merchants have pushed.
   *
   * Every filter is server-side, and `q` in particular must not be done in the
   * browser: this is cursor-paged, so a client-side filter would filter the page
   * rather than the queue, and a moderator searching a listing that happens to be
   * on page three would be told it does not exist.
   *
   * `status` names a **stored** lifecycle. `EXPIRED` is a 400 naming the field,
   * because it is derived from the window and a card whose window closed while it
   * was awaiting review is still awaiting review.
   */
  listListings: build.query({
    query: (args) => ({ url: "/marketplace/admin/listings", params: { ...args } }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("Listing", result),
    keepUnusedDataFor: 120
  }),
  /** One listing: the card as a saver would see it, plus its full moderation history. */
  getListing: build.query({
    query: (listingId) => ({ url: `/marketplace/admin/listings/${listingId}` }),
    providesTags: (result, _error, listingId) => providesOne("Listing", result ? result.listingId : listingId)
  }),
  /**
   * Approve — `PENDING_REVIEW → LIVE`.
   *
   * Invalidates `Storefront` as well as the listing: approving is the one act
   * that puts a card in front of savers, and the operator's own preview is the
   * same read the PWA calls (D14). Not maker-checker (D12) — the merchant
   * authored the card and the operator approves it, so the two-actor property
   * holds across two tenants by construction.
   */
  approveListing: build.mutation({
    query: ({ listingId }) => ({
      url: `/marketplace/admin/listings/${listingId}/approve`,
      method: "POST",
      idempotencyKey: `listing-approve:${listingId}`
    }),
    invalidatesTags: (_r, _e, { listingId }) => [
      ...invalidatesOne("Listing", listingId),
      { type: "Listing", id: "LIST" },
      { type: "Storefront", id: "LIST" }
    ]
  }),
  /**
   * Reject — the reason is required and it travels: it lands on the merchant's
   * own offer record in their Commerce console, which is the only place the card
   * can be fixed.
   */
  rejectListing: build.mutation({
    query: ({ listingId, reason }) => ({
      url: `/marketplace/admin/listings/${listingId}/reject`,
      method: "POST",
      body: { reason },
      idempotencyKey: `listing-reject:${listingId}`
    }),
    invalidatesTags: (_r, _e, { listingId }) => [
      ...invalidatesOne("Listing", listingId),
      { type: "Listing", id: "LIST" }
    ]
  }),
  /** Suspend a live card. Reversible, so the key carries a caller-minted `draftId`. */
  suspendListing: build.mutation({
    query: ({ listingId, reason, draftId }) => ({
      url: `/marketplace/admin/listings/${listingId}/suspend`,
      method: "POST",
      body: { reason, draftId },
      idempotencyKey: `listing-suspend:${listingId}:${draftId}`
    }),
    invalidatesTags: (_r, _e, { listingId }) => [
      ...invalidatesOne("Listing", listingId),
      { type: "Listing", id: "LIST" },
      { type: "Storefront", id: "LIST" }
    ]
  }),
  /** Reinstate a suspended card. No reason — see the aggregate's own note. */
  reinstateListing: build.mutation({
    query: ({ listingId, draftId }) => ({
      url: `/marketplace/admin/listings/${listingId}/reinstate`,
      method: "POST",
      body: { draftId },
      idempotencyKey: `listing-reinstate:${listingId}:${draftId}`
    }),
    invalidatesTags: (_r, _e, { listingId }) => [
      ...invalidatesOne("Listing", listingId),
      { type: "Listing", id: "LIST" },
      { type: "Storefront", id: "LIST" }
    ]
  }),
  /**
   * The Network dashboard's two marketplace tiles.
   *
   * Provides the `Listing` LIST tag, so a moderator who approves a card and returns to the
   * landing page sees a smaller "awaiting review" figure. That is not as expensive as it reads:
   * RTK Query only refetches a *subscribed* query, and while the operator is on the Marketplace
   * page this one is unmounted and merely marked stale.
   */
  getMarketplaceSnapshot: build.query({
    query: () => ({ url: "/marketplace/admin/dashboard" }),
    providesTags: () => [{ type: "Listing", id: "LIST" }],
    keepUnusedDataFor: 60
  }),
  // ─── Operator: the shelves ──────────────────────────────────────────────────
  /**
   * Every shelf of the caller's network. Unpaged, and that is a claim rather than
   * an omission: a storefront with more shelves than fit in one response is one
   * nobody can navigate.
   */
  listCollections: build.query({
    query: () => ({ url: "/marketplace/admin/collections" }),
    providesTags: (result) => result ? [
      ...result.map((row) => ({ type: "Collection", id: row.collectionId })),
      { type: "Collection", id: "LIST" }
    ] : [{ type: "Collection", id: "LIST" }],
    keepUnusedDataFor: 300
  }),
  getCollection: build.query({
    query: (collectionId) => ({ url: `/marketplace/admin/collections/${collectionId}` }),
    providesTags: (result, _error, collectionId) => providesOne("Collection", result ? result.collectionId : collectionId)
  }),
  createCollection: build.mutation({
    query: (body) => ({
      url: "/marketplace/admin/collections",
      method: "POST",
      body,
      // The id is caller-minted, which is what makes a retried create idempotent
      // rather than a second shelf.
      idempotencyKey: `collection-create:${body.collectionId}`
    }),
    invalidatesTags: [{ type: "Collection", id: "LIST" }]
  }),
  updateCollection: build.mutation({
    query: ({ collectionId, ...body }) => ({
      url: `/marketplace/admin/collections/${collectionId}`,
      method: "PUT",
      body,
      // Not @IdempotentRequest server-side: a PUT of the whole shelf is naturally
      // idempotent, and a key would replay a stale name over a newer one.
      skipIdempotencyKey: true
    }),
    invalidatesTags: (_r, _e, { collectionId }) => [
      ...invalidatesOne("Collection", collectionId),
      { type: "Collection", id: "LIST" },
      { type: "Storefront", id: "LIST" }
    ]
  }),
  /**
   * Replace the whole ordered membership.
   *
   * Value replacement, not a diff: reordering is the ordinary edit, and expressing
   * it as inserts and deletes makes two operators dragging cards at the same time
   * produce an order neither of them chose.
   */
  arrangeCollection: build.mutation({
    query: ({ collectionId, listingIds, draftId }) => ({
      url: `/marketplace/admin/collections/${collectionId}/listings`,
      method: "PUT",
      body: { listingIds, draftId },
      idempotencyKey: `collection-arrange:${collectionId}:${draftId}`
    }),
    invalidatesTags: (_r, _e, { collectionId }) => [
      ...invalidatesOne("Collection", collectionId),
      { type: "Collection", id: "LIST" },
      { type: "Storefront", id: "LIST" }
    ]
  }),
  /** Remove a shelf. The cards on it are untouched — a collection owns none of them. */
  deleteCollection: build.mutation({
    query: ({ collectionId }) => ({
      url: `/marketplace/admin/collections/${collectionId}`,
      method: "DELETE",
      idempotencyKey: `collection-delete:${collectionId}`
    }),
    invalidatesTags: (_r, _e, { collectionId }) => [
      ...invalidatesOne("Collection", collectionId),
      { type: "Collection", id: "LIST" },
      { type: "Storefront", id: "LIST" }
    ]
  }),
  // ─── Storefront (savers, and the operator's preview) ────────────────────────
  //
  // The same three reads serve the operator's preview, `apps/portfolio` and the
  // PWA's Marketplace tab. They differ in chrome and breakpoint, not in shape —
  // rendering the preview from a different query is how the three quietly become
  // three products (D14).
  /**
   * The resolved public surface: today's shelves with their cards, plus a featured
   * strip.
   *
   * **404 is a state, not a failure.** A saver whose tenant operates no network
   * gets `404`, and their app falls back to commerce-service's tenant-local
   * storefront — §8.4 gives a member jeweller's savers that read instead. The
   * client cannot decide which applies without classifying its host as "the
   * platform" versus "a tenant", which §8.2 refuses, so it asks and the server
   * answers from the session.
   */
  getStorefront: build.query({
    query: () => ({ url: "/marketplace/storefront" }),
    providesTags: () => [{ type: "Storefront", id: "LIST" }],
    keepUnusedDataFor: 300
  }),
  /**
   * "See all" — every card showing today.
   *
   * `category` and `city` are **not** parameters. §5.3 named both and neither has
   * a producer: a listing carries a benefit, not a taxonomy, and it has no branch
   * axis at all. Accepting either and ignoring it is the failure B2 refused when
   * it dropped `initiatedBy` rather than serve a filter it could not honour.
   */
  listStorefrontListings: build.query({
    query: (args) => ({ url: "/marketplace/storefront/listings", params: { ...args } }),
    ...createCursorPagedEndpoint(),
    providesTags: () => [{ type: "Storefront", id: "LIST" }],
    keepUnusedDataFor: 120
  }),
  /**
   * One card's detail. `404` for anything not showing — suspended, superseded,
   * expired, or another network's. A saver cannot act on the difference and must
   * not be able to see it.
   */
  getStorefrontListing: build.query({
    query: (listingId) => ({ url: `/marketplace/storefront/listings/${listingId}` }),
    providesTags: () => [{ type: "Storefront", id: "LIST" }]
  }),
  /**
   * A seller's page: who they are, where a saver can walk in, and what they have
   * listed. The branches are the redeemable ones, not every branch the merchant
   * owns.
   */
  getStorefrontMerchant: build.query({
    query: (sellerTenantId) => ({
      url: `/marketplace/storefront/merchants/${sellerTenantId}`
    }),
    providesTags: () => [{ type: "Storefront", id: "LIST" }]
  })
});

// src/types.ts
var MARKETPLACE_MAX_PAGE_SIZE = 100;

// src/index.ts
var marketplaceApi = createContextApi({
  context: "marketplace",
  reducerPath: "marketplaceApi",
  tagTypes: MARKETPLACE_TAGS,
  endpoints: marketplaceEndpoints
});
var {
  // Operator — moderation
  useListListingsQuery,
  useLazyListListingsQuery,
  useGetListingQuery,
  useApproveListingMutation,
  useRejectListingMutation,
  useSuspendListingMutation,
  useReinstateListingMutation,
  useGetMarketplaceSnapshotQuery,
  // Operator — merchandising
  useListCollectionsQuery,
  useGetCollectionQuery,
  useCreateCollectionMutation,
  useUpdateCollectionMutation,
  useArrangeCollectionMutation,
  useDeleteCollectionMutation,
  // Storefront — savers, and the operator's preview
  useGetStorefrontQuery,
  useListStorefrontListingsQuery,
  useGetStorefrontListingQuery,
  useGetStorefrontMerchantQuery
} = marketplaceApi;

export { MARKETPLACE_MAX_PAGE_SIZE, MARKETPLACE_TAGS, marketplaceApi, useApproveListingMutation, useArrangeCollectionMutation, useCreateCollectionMutation, useDeleteCollectionMutation, useGetCollectionQuery, useGetListingQuery, useGetMarketplaceSnapshotQuery, useGetStorefrontListingQuery, useGetStorefrontMerchantQuery, useGetStorefrontQuery, useLazyListListingsQuery, useListCollectionsQuery, useListListingsQuery, useListStorefrontListingsQuery, useReinstateListingMutation, useRejectListingMutation, useSuspendListingMutation, useUpdateCollectionMutation };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map